import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

interface FundingApplicationConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  planName: string;
  fundingSource: string;
  onConfirm: (confirmed: boolean, termsAccepted: boolean) => void;
  loading?: boolean;
}

export const FundingApplicationConfirmDialog: React.FC<FundingApplicationConfirmDialogProps> = ({
  open,
  onClose,
  planName,
  fundingSource,
  onConfirm,
  loading = false
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);

  const handleConfirm = () => {
    if (termsAccepted && reviewConfirmed) {
      onConfirm(true, true);
      // Reset state
      setTermsAccepted(false);
      setReviewConfirmed(false);
    }
  };

  const handleCancel = () => {
    setTermsAccepted(false);
    setReviewConfirmed(false);
    onClose();
  };

  const fundingSourceNames: Record<string, string> = {
    funding_circle: 'Funding Circle',
    sba: 'SBA (Small Business Administration)',
    uk_startup_loans: 'UK Start Up Loans'
  };

  const checklistItems = [
    'All information in the business plan is accurate and complete',
    'Financial projections have been reviewed and verified',
    'You understand this is a legally binding application',
    'You have reviewed all terms and conditions',
    'You are authorized to submit this application on behalf of the business'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" fontSize="large" />
          <Typography variant="h6" component="span">
            Confirm Funding Application Submission
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            You are about to submit a funding application
          </Typography>
          <Typography variant="body2">
            <strong>Business Plan:</strong> {planName}
          </Typography>
          <Typography variant="body2">
            <strong>Funding Source:</strong> {fundingSourceNames[fundingSource] || fundingSource}
          </Typography>
        </Alert>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Please review and confirm:
        </Typography>

        <List dense>
          {checklistItems.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={item}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> This action cannot be undone. {fundingSourceNames[fundingSource]} 
            will review your application and may contact you for additional information.
          </Typography>
        </Alert>

        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={reviewConfirmed}
                onChange={(e) => setReviewConfirmed(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="body2">
                I have reviewed all information and confirm it is accurate and complete
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="body2">
                I accept the terms and conditions and understand this is a legally binding application
              </Typography>
            }
          />
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Disclaimer:</strong> AI-generated content in this business plan should be reviewed 
            by qualified professionals. This system provides tools and suggestions but is not a substitute 
            for professional financial, legal, or business advice.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleCancel} 
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!termsAccepted || !reviewConfirmed || loading}
          startIcon={loading ? null : <CheckCircleIcon />}
        >
          {loading ? 'Submitting...' : 'Confirm & Submit Application'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FundingApplicationConfirmDialog;
